import { CONNECTIVITY_UI_OFFLINE_GRACE_MS } from './connectivity-policy';

const BACKEND_HEALTH_PATH = '/api/health';
const BACKEND_BANDWIDTH_PROBE_PATH = '/api/bandwidth-probe';
const SAME_ORIGIN_BANDWIDTH_FALLBACK_PATH = '/';
const BACKEND_HEALTH_TIMEOUT_MS = 2500;
const BACKEND_HEALTH_MAX_RETRY_MS = 5000;
const BACKEND_HEALTH_EVENT = 'streamlain:backend-availability';
const BACKEND_AVAILABLE_EVENT = 'streamlain:backend-available';
const PASSIVE_BANDWIDTH_MAX_AGE_MS = 60_000;
const PASSIVE_BANDWIDTH_MIN_ENTRY_BYTES = 8 * 1024;
const PASSIVE_BANDWIDTH_MIN_TOTAL_BYTES = 16 * 1024;
const PASSIVE_BANDWIDTH_EXCLUDED_PATHS = new Set([
  BACKEND_HEALTH_PATH,
  BACKEND_BANDWIDTH_PROBE_PATH,
]);
const PASSIVE_BANDWIDTH_INITIATOR_TYPES = new Set(['fetch', 'xmlhttprequest']);

const BACKEND_UNAVAILABLE_MESSAGE_PARTS = [
  'network_error',
  'network error',
  'failed to fetch',
  'fetch failed',
  'load failed',
  'signal timed out',
  'timed out',
  'timeout',
  'networkerror when attempting to fetch resource',
  'network request failed',
  'server is not available',
  'server returned an empty or invalid response',
  'unexpected end of json input',
  'invalid json response',
  'gateway timeout',
  'error occurred while trying to proxy',
  'bad gateway',
  'econnrefused',
  'econnreset',
  'enotfound',
  'err_connection_refused',
  'err_network',
] as const;

const BACKEND_AUTH_OR_PERMISSION_CODES = new Set([
  '401',
  '403',
  'access_denied',
  'action_forbidden',
  'action_forbidden_on_non_team_workspace',
  'authentication_required',
  'forbidden',
  'not_authenticated',
  'permission_denied',
  'unauthenticated',
]);

function collectErrorSignals(
  input: unknown,
  signals: string[],
  seen: WeakSet<object>
): void {
  if (input === null || input === undefined) return;

  if (typeof input === 'string' || typeof input === 'number') {
    signals.push(String(input));
    return;
  }

  if (input instanceof Error) {
    signals.push(input.name, input.message);
    collectErrorSignals(input.cause, signals, seen);
    return;
  }

  if (typeof input !== 'object') return;
  if (seen.has(input)) return;
  seen.add(input);

  const record = input as Record<string, unknown>;
  for (const key of [
    'name',
    'message',
    'type',
    'code',
    'status',
    'statusCode',
  ]) {
    const value = record[key];
    if (typeof value === 'string' || typeof value === 'number') {
      signals.push(String(value));
    }
  }

  const response = record.response;
  if (response && typeof response === 'object') {
    const status = (response as Record<string, unknown>).status;
    if (typeof status === 'string' || typeof status === 'number') {
      signals.push(String(status));
    }
  }

  collectErrorSignals(record.cause, signals, seen);
  collectErrorSignals(record.extensions, signals, seen);
  collectErrorSignals(record.networkError, signals, seen);
  collectErrorSignals(record.response, signals, seen);

  for (const key of ['errors', 'graphQLErrors']) {
    const errors = record[key];
    if (Array.isArray(errors)) {
      for (const error of errors) {
        collectErrorSignals(error, signals, seen);
      }
    }
  }
}

export function isBackendUnavailableError(error: unknown): boolean {
  if (error === null || error === undefined) return false;

  const signals: string[] = [];
  collectErrorSignals(error, signals, new WeakSet());

  const message = signals.join(' ').toLowerCase();
  return BACKEND_UNAVAILABLE_MESSAGE_PARTS.some(part => message.includes(part));
}

export function isBackendAuthOrPermissionError(error: unknown): boolean {
  if (error === null || error === undefined) return false;

  const signals: string[] = [];
  collectErrorSignals(error, signals, new WeakSet());

  return signals.some(signal =>
    BACKEND_AUTH_OR_PERMISSION_CODES.has(signal.toLowerCase())
  );
}

let backendAvailable = true;
let backendProbeSuccessRevision = 0;
let unavailableSince: number | null = backendAvailable ? null : Date.now();
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let activeHealthProbe: Promise<BackendLatencyProbeResult> | null = null;
let activeHealthProbeShouldScheduleRetry = false;
let activeBandwidthProbe: Promise<number | null> | null = null;
let consecutiveProbeFailures = 0;
let listenersInitialized = false;
let uiBackendAvailable = true;
let uiOfflineTimer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<() => void>();
const uiListeners = new Set<() => void>();

function emitUiAvailabilityChanged(): void {
  for (const listener of uiListeners) {
    listener();
  }
}

function setUiBackendAvailable(next: boolean): void {
  if (uiBackendAvailable === next) return;
  uiBackendAvailable = next;
  emitUiAvailabilityChanged();
}

function clearUiOfflineTimer(): void {
  if (!uiOfflineTimer) return;
  clearTimeout(uiOfflineTimer);
  uiOfflineTimer = null;
}

function reconcileUiBackendAvailability(): void {
  const rawAvailable = getBackendAvailabilitySnapshot();
  if (rawAvailable) {
    clearUiOfflineTimer();
    setUiBackendAvailable(true);
    return;
  }

  if (!uiBackendAvailable || uiOfflineTimer) return;
  const offlineElapsedMs =
    unavailableSince === null ? 0 : Date.now() - unavailableSince;
  const remainingGraceMs = Math.max(
    0,
    CONNECTIVITY_UI_OFFLINE_GRACE_MS - offlineElapsedMs
  );
  if (remainingGraceMs === 0) {
    setUiBackendAvailable(false);
    return;
  }
  uiOfflineTimer = setTimeout(() => {
    uiOfflineTimer = null;
    if (!getBackendAvailabilitySnapshot()) {
      setUiBackendAvailable(false);
    }
  }, remainingGraceMs);
}

function emitAvailabilityChanged(wasAvailable: boolean): void {
  for (const listener of listeners) {
    listener();
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BACKEND_HEALTH_EVENT));
    if (!wasAvailable && backendAvailable) {
      window.dispatchEvent(new CustomEvent(BACKEND_AVAILABLE_EVENT));
    }
  }
}

function setBackendAvailable(next: boolean): void {
  const wasAvailable = backendAvailable;
  backendAvailable = next;
  unavailableSince = next ? null : (unavailableSince ?? Date.now());
  if (next) {
    consecutiveProbeFailures = 0;
    if (probeTimer) {
      clearTimeout(probeTimer);
      probeTimer = null;
    }
  }
  reconcileUiBackendAvailability();
  if (wasAvailable !== next) {
    emitAvailabilityChanged(wasAvailable);
  }
}

function nextProbeDelayMs(): number {
  const base = Math.min(
    1000 * Math.pow(2, Math.max(0, consecutiveProbeFailures - 1)),
    BACKEND_HEALTH_MAX_RETRY_MS
  );
  return base + Math.floor(Math.random() * 250);
}

function initBackendAvailabilityListeners(): void {
  if (listenersInitialized || typeof window === 'undefined') return;
  listenersInitialized = true;

  window.addEventListener('offline', () => {
    consecutiveProbeFailures = 0;
    void probeBackendAvailabilityNow();
  });
  window.addEventListener('online', () => {
    consecutiveProbeFailures = 0;
    void probeBackendAvailabilityNow();
  });
  window.addEventListener('focus', () => {
    if (!backendAvailable) {
      void probeBackendAvailabilityNow();
    }
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !backendAvailable) {
        void probeBackendAvailabilityNow();
      }
    });
  }
}

function scheduleBackendProbe(delayMs = nextProbeDelayMs()): void {
  if (backendAvailable) return;
  if (typeof window === 'undefined') return;
  if (probeTimer) return;

  probeTimer = setTimeout(() => {
    probeTimer = null;
    void probeBackendAvailabilityNow();
  }, delayMs);
}

export function getBackendAvailabilitySnapshot(): boolean {
  return backendAvailable;
}

export function getBackendProbeSuccessRevision(): number {
  return backendProbeSuccessRevision;
}

export function getBackendAvailabilityUiSnapshot(): boolean {
  return uiBackendAvailable;
}

export function getBackendUnavailableSince(): number | null {
  return getBackendAvailabilitySnapshot() ? null : unavailableSince;
}

export function subscribeBackendAvailability(listener: () => void): () => void {
  initBackendAvailabilityListeners();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isBackendAvailableForImmediateRequest(): boolean {
  return getBackendAvailabilitySnapshot();
}

export function subscribeBackendRecovery(listener: () => void): () => void {
  return subscribeBackendAvailability(() => {
    if (getBackendAvailabilitySnapshot()) {
      listener();
    }
  });
}

export function subscribeBackendAvailabilityUi(
  listener: () => void
): () => void {
  initBackendAvailabilityListeners();
  reconcileUiBackendAvailability();
  uiListeners.add(listener);
  return () => uiListeners.delete(listener);
}

export function isBackendKnownUnavailable(): boolean {
  return !getBackendAvailabilityUiSnapshot();
}

export function canAttemptBackendRequest(): boolean {
  return getBackendAvailabilitySnapshot();
}

export function markBackendAvailable(): void {
  setBackendAvailable(true);
}

export function markBackendUnavailable(error?: unknown): void {
  if (error !== undefined && !isBackendUnavailableError(error)) {
    return;
  }
  setBackendAvailable(false);
  scheduleBackendProbe(0);
}

type BackendLatencyProbeResult = {
  available: boolean;
  bandwidthMbps: number | null;
  latencyMs: number | null;
  status?: number;
};

type BackendLatencyProbeOptions = {
  measureBandwidth?: boolean;
  scheduleRetryOnFailure?: boolean;
};

type PassiveBandwidthResourceTiming = Pick<
  PerformanceResourceTiming,
  | 'encodedBodySize'
  | 'initiatorType'
  | 'name'
  | 'responseEnd'
  | 'responseStart'
  | 'transferSize'
>;

const nowMs = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

export function estimatePassiveBackendBandwidthMbps(
  entries: readonly PassiveBandwidthResourceTiming[],
  options: {
    maxAgeMs?: number;
    minEntryBytes?: number;
    minTotalBytes?: number;
    nowMs?: number;
    origin?: string;
  } = {}
): number | null {
  const maxAgeMs = options.maxAgeMs ?? PASSIVE_BANDWIDTH_MAX_AGE_MS;
  const minEntryBytes =
    options.minEntryBytes ?? PASSIVE_BANDWIDTH_MIN_ENTRY_BYTES;
  const minTotalBytes =
    options.minTotalBytes ?? PASSIVE_BANDWIDTH_MIN_TOTAL_BYTES;
  const now = options.nowMs ?? nowMs();
  const origin =
    options.origin ??
    (typeof window !== 'undefined' ? window.location.origin : null);
  if (!origin) return null;

  let totalBytes = 0;
  let totalDurationMs = 0;

  for (const entry of entries) {
    if (!PASSIVE_BANDWIDTH_INITIATOR_TYPES.has(entry.initiatorType)) continue;
    if (now - entry.responseEnd > maxAgeMs) continue;
    if (entry.responseEnd <= entry.responseStart) continue;

    let url: URL;
    try {
      url = new URL(entry.name, origin);
    } catch {
      continue;
    }
    if (url.origin !== origin) continue;
    if (PASSIVE_BANDWIDTH_EXCLUDED_PATHS.has(url.pathname)) continue;

    const bytes = entry.transferSize;
    if (!Number.isFinite(bytes) || bytes < minEntryBytes) continue;

    totalBytes += bytes;
    totalDurationMs += entry.responseEnd - entry.responseStart;
  }

  if (totalBytes < minTotalBytes || totalDurationMs <= 0) return null;
  return Number(((totalBytes * 8) / totalDurationMs / 1000).toFixed(1));
}

function estimatePassiveBackendBandwidthFromPerformance(): number | null {
  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return null;
  }

  return estimatePassiveBackendBandwidthMbps(
    performance.getEntriesByType('resource') as PerformanceResourceTiming[]
  );
}

export function estimateActiveBackendBandwidthMbps(
  bytes: number,
  durationMs: number
): number | null {
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
  return Number(((bytes * 8) / durationMs / 1000).toFixed(1));
}

async function fetchBandwidthProbePacket(
  url: string,
  signal: AbortSignal
): Promise<number | null> {
  const startedAt = nowMs();
  const res = await globalThis.fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    signal,
    headers: {
      'x-streamlain-bandwidth-probe': '1',
    },
  });
  if (!res.ok) return null;

  const body = await res.arrayBuffer();
  return estimateActiveBackendBandwidthMbps(
    body.byteLength,
    Math.max(1, nowMs() - startedAt)
  );
}

async function runActiveBackendBandwidthProbe(): Promise<number | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(new Error('backend bandwidth probe timeout')),
    BACKEND_HEALTH_TIMEOUT_MS
  );
  const cacheBuster = Math.trunc(Date.now()).toString(36);
  try {
    return (
      (await fetchBandwidthProbePacket(
        `${BACKEND_BANDWIDTH_PROBE_PATH}?t=${cacheBuster}`,
        controller.signal
      )) ??
      (await fetchBandwidthProbePacket(
        `${SAME_ORIGIN_BANDWIDTH_FALLBACK_PATH}?streamlain-bandwidth-probe=${cacheBuster}`,
        controller.signal
      ))
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

function probeActiveBackendBandwidthMbps(): Promise<number | null> {
  if (activeBandwidthProbe) return activeBandwidthProbe;

  activeBandwidthProbe = runActiveBackendBandwidthProbe().finally(() => {
    activeBandwidthProbe = null;
  });
  return activeBandwidthProbe;
}

function probeBackendHealthNow(
  scheduleRetryOnFailure: boolean
): Promise<BackendLatencyProbeResult> {
  initBackendAvailabilityListeners();
  activeHealthProbeShouldScheduleRetry ||= scheduleRetryOnFailure;
  if (activeHealthProbe) return activeHealthProbe;

  activeHealthProbe = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(new Error('backend health timeout')),
      BACKEND_HEALTH_TIMEOUT_MS
    );
    const startedAt = nowMs();

    try {
      const res = await globalThis.fetch(BACKEND_HEALTH_PATH, {
        cache: 'no-store',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'x-streamlain-health-probe': '1',
        },
      });
      const latencyMs = Math.max(0, Math.round(nowMs() - startedAt));
      if (res.ok) {
        backendProbeSuccessRevision += 1;
        setBackendAvailable(true);
        return {
          available: true,
          bandwidthMbps: null,
          latencyMs,
          status: res.status,
        };
      }

      consecutiveProbeFailures += 1;
      setBackendAvailable(false);
      if (activeHealthProbeShouldScheduleRetry) {
        scheduleBackendProbe();
      }
      return {
        available: false,
        bandwidthMbps: null,
        latencyMs,
        status: res.status,
      };
    } catch {
      consecutiveProbeFailures += 1;
      setBackendAvailable(false);
      if (activeHealthProbeShouldScheduleRetry) {
        scheduleBackendProbe();
      }
      return { available: false, bandwidthMbps: null, latencyMs: null };
    } finally {
      clearTimeout(timeoutId);
      activeHealthProbe = null;
      activeHealthProbeShouldScheduleRetry = false;
    }
  })();

  return activeHealthProbe;
}

export async function probeBackendLatencyNow(
  options: BackendLatencyProbeOptions = {}
): Promise<BackendLatencyProbeResult> {
  const measureBandwidth = options.measureBandwidth ?? false;
  const scheduleRetryOnFailure = options.scheduleRetryOnFailure ?? true;
  const result = await probeBackendHealthNow(scheduleRetryOnFailure);
  if (!result.available) return result;

  let bandwidthMbps = estimatePassiveBackendBandwidthFromPerformance();
  if (!measureBandwidth) {
    return {
      ...result,
      bandwidthMbps,
    };
  }
  try {
    bandwidthMbps = (await probeActiveBackendBandwidthMbps()) ?? bandwidthMbps;
  } catch {
    bandwidthMbps = bandwidthMbps ?? null;
  }
  return {
    ...result,
    bandwidthMbps,
  };
}

export async function probeBackendAvailabilityNow(): Promise<boolean> {
  return (await probeBackendHealthNow(true)).available;
}

export function onBackendAvailable(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => listener();
  window.addEventListener(BACKEND_AVAILABLE_EVENT, handler);
  return () => window.removeEventListener(BACKEND_AVAILABLE_EVENT, handler);
}
